import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import * as Common from '_common';
import * as Components from '_components';
import { frames } from '_frameRegister';
import * as Styles from './PageFrames.module.scss';
export var FrameSize;
(function (FrameSize) {
    FrameSize[FrameSize["Minimized"] = 0] = "Minimized";
    FrameSize[FrameSize["Normal"] = 1] = "Normal";
    FrameSize[FrameSize["FullScreen"] = 2] = "FullScreen";
})(FrameSize || (FrameSize = {}));
export const FrameContext = React.createContext(undefined);
export function FrameProvider(props) {
    const [frames, setFrames] = React.useState(() => {
        const storedFrames = localStorage.getItem('frames');
        if (storedFrames) {
            return JSON.parse(storedFrames);
        }
        return [
            {
                frame: {
                    id: 'default',
                    title: 'Default Frame',
                    component: { type: 'Widgets_TimeSmall', properties: {} },
                },
                size: FrameSize.Normal,
            },
        ];
    });
    React.useEffect(() => {
        localStorage.setItem('frames', JSON.stringify(frames));
    }, [frames]);
    return (_jsx(FrameContext.Provider, { value: [frames, setFrames], children: props.children }));
}
export function useFrameRegister() {
    const contextValue = React.useContext(FrameContext);
    if (!contextValue) {
        throw new Error('useFrameRegister must be used within a FrameProvider');
    }
    const [frames, setFrames] = contextValue;
    function register(frame) {
        if (frames.find(f => f.frame.id === frame.id)) {
            return;
        }
        const newFrameState = { frame, size: FrameSize.Normal };
        setFrames(prevFrames => [...prevFrames, newFrameState]);
    }
    function remove(id) {
        setFrames(prevFrames => prevFrames.filter(frame => frame.frame.id !== id));
    }
    function setSize(frame, size) {
        if (frame.size === size) {
            return frame;
        }
        return { ...frame, size };
    }
    function maximize(id) {
        setFrames(prevFrames => prevFrames.map(frame => frame.frame.id === id
            ? setSize(frame, FrameSize.FullScreen)
            : setSize(frame, FrameSize.Normal)));
    }
    function closeMaximized() {
        setFrames(prevFrames => prevFrames.map(frame => setSize(frame, FrameSize.Normal)));
    }
    function frame(id) {
        return frames.find(frame => frame.frame.id === id);
    }
    function updateFrame(id, frame) {
        setFrames(prevFrames => prevFrames.map(f => (f.frame.id === id ? frame : f)));
    }
    return {
        frames,
        register,
        remove,
        maximize,
        closeMaximized,
        frame,
        updateFrame,
    };
}
export function NormalFrames() {
    const frameRegister = Common.useFrameRegister();
    const frames = frameRegister.frames.filter(frame => frame.size === FrameSize.Normal);
    if (frames.length === 0) {
        return null;
    }
    return (_jsx("div", { className: Styles.main_frame_area, children: _jsx(Common.ErrorBoundary, { children: frames.map(frame => (_jsx(Frame, { ...frame.frame }, frame.frame.id))) }) }));
}
export function MinimizedFrames() {
    const frameRegister = Common.useFrameRegister();
    const overlay = Common.useOverlay();
    const frames = frameRegister.frames.filter(frame => frame.size === FrameSize.Minimized);
    let minizedFrames = null;
    if (frames.length > 0) {
        minizedFrames = frames.map(frame => (_jsx(Frame, { ...frame.frame }, frame.frame.id)));
    }
    return (_jsx(Components.FrameSegment, { children: _jsx(Common.ErrorBoundary, { children: _jsxs(Components.BtnGroup, { children: [minizedFrames, _jsx(Components.Button, { onClick: () => overlay.setOverlay({
                            type: Common.OverlayType.FrameRegister,
                        }), children: "+" })] }) }) }));
}
export function Frame(props) {
    const frameRegister = Common.useFrameRegister();
    const state = frameRegister.frame(props.id);
    const [positionState, setPosition] = React.useState({});
    const [resizeState, setResizeState] = React.useState({ dragType: 'none', initialX: 0, initialY: 0 });
    const ref = React.useRef(null);
    if (!props.component) {
        // TODO : Show error component
        return null;
    }
    const componentToRender = {
        ...props.component,
        properties: {
            ...props.component?.properties,
            frameSize: props.component?.properties?.frameSize ?? FrameSize.Normal,
        },
    };
    if (!state) {
        return null;
    }
    if (state.size === FrameSize.Minimized) {
        return (_jsx(Components.Button, { onClick: () => {
                frameRegister.updateFrame(props.id, {
                    ...state,
                    size: FrameSize.Normal,
                });
            }, children: _jsx(Components.MLT, { text: props.title ?? props.id }) }));
    }
    return (_jsx("div", { className: Styles.frame, style: positionState, ref: ref, children: _jsxs(Components.Frame, { children: [_jsxs(Components.FrameComponent, { side: Components.FrameComponentType.Top, type: Components.FrameSideType.Big, children: [_jsx(Components.FrameSegment, { children: _jsx("h1", { children: props.title ? (_jsx(Components.MLT, { text: props.title })) : (props.id) }) }), _jsx(Components.FrameSegment, { type: Components.FrameSegmentType.Empty }), _jsx(Components.FrameSegment, { children: _jsxs(Components.BtnGroup, { children: [_jsx(Components.Button, { onClick: () => {
                                            frameRegister.updateFrame(props.id, {
                                                ...state,
                                                size: FrameSize.Minimized,
                                            });
                                        }, children: "_" }), _jsx(Components.Button, { onClick: () => frameRegister.maximize(props.id), children: "[ ]" }), _jsx(Components.Button, { onClick: () => frameRegister.remove(props.id), children: "X" })] }) })] }), _jsx(Components.FrameComponent, { side: Components.FrameComponentType.Left, type: Components.FrameSideType.Small }), _jsx(Components.FrameComponent, { side: Components.FrameComponentType.Right, type: Components.FrameSideType.Small }), _jsx(Components.FrameComponent, { side: Components.FrameComponentType.Bottom, type: Components.FrameSideType.Small }), _jsx(Components.FrameComponent, { side: Components.FrameComponentType.Content, children: _jsx("div", { className: Styles.frame_content, children: _jsx(Components.Content, { component: componentToRender }) }) })] }) }));
}
export function FrameSelection() {
    const frameRegister = Common.useFrameRegister();
    const [widgets, _] = Common.useWidgets();
    const overlayContext = Common.useOverlay();
    function showFrame(frame) {
        frameRegister.register(frame);
        overlayContext.setOverlay({
            type: Common.OverlayType.Null,
        });
    }
    return (_jsxs(_Fragment, { children: [_jsx(Components.BtnGroup, { children: widgets.map(widget => (_jsx(Components.Button, { children: _jsx(Components.MLT, { text: widget.title }) }, widget.id))) }), _jsx("hr", {}), _jsx(Components.BtnGroup, { children: frames.map(frame => (_jsx(Components.Button, { onClick: () => {
                        showFrame(frame);
                    }, children: _jsx(Components.MLT, { text: frame.title }) }, frame.id))) })] }));
}
